import { z } from 'zod';

export const Allergen = z.enum([
  'nuts',
  'peanut',
  'shellfish',
  'fish',
  'egg',
  'soy',
  'wheat',
  'sesame',
  'dairy',
  'pork',
  'alcohol',
]);
export type Allergen = z.infer<typeof Allergen>;

export const DietaryTag = z.enum([
  'vegan',
  'vegetarian',
  'pescatarian',
  'halal',
  'kosher',
  'gluten_free',
  'dairy_free',
]);
export type DietaryTag = z.infer<typeof DietaryTag>;

export const MealContext = z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'drink']);
export type MealContext = z.infer<typeof MealContext>;

export const Portion = z.enum(['small', 'medium', 'large', 'sharing']);
export type Portion = z.infer<typeof Portion>;

// Hebrew dietary categorization for the kosher meat+dairy separation rule.
// - meat: beef, chicken, lamb, etc. (flesh of land animals)
// - dairy: contains milk, cheese, butter, cream, yogurt
// - pareve: neither (fish, eggs, plant-only)
// - unknown: Gemini couldn't determine; safest is to ask the kitchen
// Drives the kosher meal-mode engine — adding a dairy dish to a meat order
// (or vice versa) fires a contradiction guard for kosher-identity users.
export const KosherCategory = z.enum(['meat', 'dairy', 'pareve', 'unknown']);
export type KosherCategory = z.infer<typeof KosherCategory>;

// Most fields are .nullish() (null | undefined) and normalized to null after
// parse — the Gemini schema marks them optional so the model can omit them
// entirely on dishes where they don't apply, saving output tokens.
export const Dish = z.object({
  id: z.string(),
  nameOriginal: z.string().min(1),
  nameTranslated: z.string().min(1),
  descriptionOriginal: z.string().nullish().transform((v) => v ?? null),
  descriptionTranslated: z.string().nullish().transform((v) => v ?? null),
  culturalContext: z.string().nullish().transform((v) => v ?? null),
  price: z.number().nonnegative().nullish().transform((v) => v ?? null),
  priceText: z.string().nullish().transform((v) => v ?? null),
  mealContext: z.array(MealContext).default([]),
  portion: Portion.nullish().transform((v) => v ?? null),
  dietaryTags: z.array(DietaryTag).default([]),
  allergens: z.array(Allergen).default([]),
  allergenUncertainty: z.array(Allergen).default([]),
  spiceLevel: z.number().int().min(0).max(3).nullish().transform((v) => v ?? null),
  // For fish dishes only: true if the fish has scales AND fins (kosher),
  // false if it doesn't (catfish, eel, shark, swordfish, monkfish), null
  // if not a fish dish or Gemini couldn't tell. Drives the kosher diet
  // filter — non-scaled fish are not permitted.
  fishKosher: z.boolean().nullish().transform((v) => v ?? null),
  // Kosher meat/dairy/pareve classification. Drives the meal-mode engine
  // for kosher users (a dairy dish added to a meat-mode cart fires the
  // contradiction guard). Defaults to 'unknown' when Gemini omits it,
  // which is treated as safe (no contradiction).
  kosherCategory: KosherCategory.nullish().transform((v) => v ?? 'unknown'),
  confidence: z.number().min(0).max(1),
});
export type Dish = z.infer<typeof Dish>;

export const Category = z.object({
  id: z.string(),
  nameOriginal: z.string().min(1),
  nameTranslated: z.string().min(1),
  dishes: z.array(Dish),
});
export type Category = z.infer<typeof Category>;

// Menu-level notes carry global information Gemini noticed about the whole
// menu: pricing gotchas (coperto, service charge, market price), safety
// (halal-only kitchen, shared fryer), info (prix-fixe structure, hours), and
// parse-quality ("page 2 cut off"). Each tier is rendered differently.
export const MenuNoteSeverity = z.enum(['pricing', 'safety', 'info', 'parse']);
export type MenuNoteSeverity = z.infer<typeof MenuNoteSeverity>;

export const MenuNote = z.object({
  text: z.string(),
  severity: MenuNoteSeverity,
});
export type MenuNote = z.infer<typeof MenuNote>;

export const Menu = z.object({
  restaurantName: z.string().nullish().transform((v) => v ?? null),
  restaurantConfidence: z.number().min(0).max(1).default(0),
  detectedSourceLanguage: z.string().min(2).max(8),
  targetLanguage: z.string().min(2).max(8),
  currency: z.string().length(3).nullish().transform((v) => v ?? null),
  notes: z.array(MenuNote).default([]),
  categories: z.array(Category),
  // data: URIs of the original captured menu pages (compressed for display).
  pageImages: z.array(z.string()).default([]),
});

// Confidence thresholds shared between client and server.
// - Pro-retry fires on the server when min dish confidence is below this.
// - Visible "uncertain translation" chip shows on the client below this.
// - Menu-level "translation quality is low" strip replaces per-dish chips
//   when the ratio of flagged dishes exceeds MENU_LEVEL_LOW_CONFIDENCE_RATIO.
export const DISH_LOW_CONFIDENCE_THRESHOLD = 0.45;
export const MENU_LEVEL_LOW_CONFIDENCE_RATIO = 0.5;

// Walk a cart against its menu and return the current meal mode for kosher's
// meat+dairy separation rule. Returns 'meat' if any line's dish is meat-class,
// 'dairy' if any line is dairy-class (and no meat-class present), or null if
// the cart is empty / pareve-only / all-unknown. Used by DishSheet's
// contradiction guard for kosher users — adding a dairy dish to a meat cart
// (or vice versa) fires the dialog.
export function deriveMealMode(menu: Menu | null, cart: Cart | null): 'meat' | 'dairy' | null {
  if (!menu || !cart) return null;
  let hasMeat = false;
  let hasDairy = false;
  for (const line of cart.lines) {
    const found = findDish(menu, line.dishId);
    if (!found) continue;
    if (found.dish.kosherCategory === 'meat') hasMeat = true;
    if (found.dish.kosherCategory === 'dairy') hasDairy = true;
  }
  if (hasMeat) return 'meat';
  if (hasDairy) return 'dairy';
  return null;
}

// Conservative derivation of "is this dish likely safe for {tag}" — used by
// the menu-screen diet filter pill (e.g. "Vegan only", "Halal-friendly only").
// Trusts Gemini's positive tag if present. Otherwise derives from the absence
// of conflicting allergens, including the allergenUncertainty list (we err
// toward hiding ambiguous matches for filter purposes — a user who tapped
// "Vegan only" doesn't want to scroll past "maybe contains fish").
//
// Hidden ingredients (fish sauce, lard, dashi, stock cubes) aren't catchable
// from the allergen array — those are surfaced via the no_fish_sauce /
// no_pork_lard strict tags and the cuisine-aware pre-scan coach.
export function isLikelyForDiet(dish: Dish, tag: DietaryTag): boolean {
  if (dish.dietaryTags.includes(tag)) return true;
  const allConflicts = [...dish.allergens, ...dish.allergenUncertainty];
  switch (tag) {
    case 'vegan':
      return !allConflicts.some((a) => (['fish', 'shellfish', 'dairy', 'egg', 'pork'] as Allergen[]).includes(a));
    case 'vegetarian':
      return !allConflicts.some((a) => (['fish', 'shellfish', 'pork'] as Allergen[]).includes(a));
    case 'pescatarian':
      return !allConflicts.includes('pork');
    case 'halal':
      return !allConflicts.some((a) => (['pork', 'alcohol'] as Allergen[]).includes(a));
    case 'kosher': {
      const hasNonKosherAllergen = allConflicts.some((a) => (['pork', 'shellfish'] as Allergen[]).includes(a));
      // Catfish, eel, shark, swordfish lack scales+fins → not kosher even
      // though they're "fish." Gemini emits fishKosher === false for these.
      const isNonKosherFish = dish.fishKosher === false;
      return !hasNonKosherAllergen && !isNonKosherFish;
    }
    case 'gluten_free':
      return !allConflicts.includes('wheat');
    case 'dairy_free':
      return !allConflicts.includes('dairy');
  }
}
export type Menu = z.infer<typeof Menu>;

// Rich on-demand dish context surfaced behind the "What is this dish?"
// button on DishSheet. Fetched per-dish via /api/menu/:menuId/dish/:dishId/explain
// when the user signals they want depth. Hand-off card and allergen flags
// remain the trusted safety layers — this object is declarative cultural +
// preparation context so the user can decide yes/no, never an authoritative
// ingredient list.
export const DishExplanation = z.object({
  // ONE sentence, ≤30 words. What this dish IS, plus one sensory cue.
  // Declarative, never "this is said to be..." — just say what it is.
  identity: z.string().min(1),
  // 1-3 declarative "things to know" — cultural heads-ups and preparation
  // oddities the user needs to make a yes/no decision: "This is organ meat,"
  // "served raw," "tiny portion meant to share," "contains lard in the base."
  // Not conversation prompts. The user usually can't have that conversation
  // through a language barrier — they need enough to decide themselves.
  caveats: z.array(z.string()).default([]),
  // ≤20 words. Cooking method when it matters for expectation (raw,
  // fermented, deep-fried, served cold). Skip when redundant with identity.
  prep: z.string().default(''),
  // ≤25 words. At-the-table reality the user can't infer — sharing,
  // plating surprise, actual spice reality vs. menu description.
  experience: z.string().default(''),
  // Optional pairing or sharing tip grounded in OTHER dishes on the same menu.
  proTip: z.string().nullish().transform((v) => v ?? null),
  // 0-2 SAFETY-CRITICAL red flags only — fugu toxicity, raw poultry,
  // shared-fryer cross-contamination. Declarative. This is the "heads up"
  // surfaced in a red box. Allergy safety is NOT handled here — the user's
  // strict-tag + hand-off flow is the authoritative safety system.
  hiddenWarnings: z.array(z.string()).default([]),
});
export type DishExplanation = z.infer<typeof DishExplanation>;

// Stable hash of the parts of a Dish that, if changed, mean a cached
// explanation no longer matches the dish. Used by both server-side and
// client-side caches to invalidate on re-parse. FNV-1a 32-bit; portable
// across Node + browser without crypto. Not for security — just for staleness.
export function dishHash(dish: Pick<Dish, 'allergens' | 'nameOriginal' | 'nameTranslated'>): string {
  const sortedAllergens = [...dish.allergens].sort().join(',');
  const input = `${sortedAllergens}|${dish.nameOriginal}|${dish.nameTranslated}`;
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export const ExplainDishRequest = z.object({
  targetLanguage: z.string().min(2).max(8),
  // Optional reason from the user's onboarding, used to add a halal/kosher
  // meat-source closer to the explanation when relevant.
  dietaryReason: z.enum(['medical', 'celiac', 'halal', 'kosher', 'ethical']).optional(),
});
export type ExplainDishRequest = z.infer<typeof ExplainDishRequest>;

export interface ExplainDishResponse {
  explanation: DishExplanation;
  cached: boolean;
  dishHash: string;
}

export const ParseRequest = z.object({
  targetLanguage: z.string().min(2).max(8),
  // When set, the server prepends the previous menu's stored page images to
  // the new uploads and re-parses everything together. Powers "Add page" from
  // MenuScreen. Result is a brand-new menuId (hash of combined image set).
  previousMenuId: z.string().min(1).max(64).optional(),
});
export type ParseRequest = z.infer<typeof ParseRequest>;

export const ParseResponse = z.object({
  menuId: z.string(),
  menu: Menu,
  cached: z.boolean(),
  modelUsed: z.string(),
});
export type ParseResponse = z.infer<typeof ParseResponse>;

// Cart-side dietary concern. Distinct from the dish's own DietaryTag (which
// describes the dish positively, e.g. "this is vegan"). Concerns are user-
// authored asks: "I want this dish to be vegan" or "no fish sauce". Includes
// cuisine-critical entries (fish sauce, pork lard, cross-contamination) that
// hide in dishes labeled "vegetarian" abroad.
export const DietaryConcernTag = z.enum([
  'vegan',
  'vegetarian',
  'gluten_free',
  'no_nuts',
  'no_dairy',
  'no_pork',
  'no_shellfish',
  'no_alcohol',
  'no_fish_sauce',
  'no_pork_lard',
  'no_cross_contamination',
]);
export type DietaryConcernTag = z.infer<typeof DietaryConcernTag>;

export const DietaryConcernSeverity = z.enum(['preference', 'strict']);
export type DietaryConcernSeverity = z.infer<typeof DietaryConcernSeverity>;

// Why the user picked strict on a dish — drives reason-aware phrasing on
// the hand-off screen (medical vs religious vs ethical framing). Captured
// once per device on the first strict tap and applied to subsequent tags
// unless the user changes it. Only meaningful for severity === 'strict'.
export const DietaryConcernReason = z.enum(['medical', 'celiac', 'halal', 'kosher', 'ethical']);
export type DietaryConcernReason = z.infer<typeof DietaryConcernReason>;

export const DietaryConcern = z.object({
  tag: DietaryConcernTag,
  severity: DietaryConcernSeverity,
  reason: DietaryConcernReason.optional(),
});
export type DietaryConcern = z.infer<typeof DietaryConcern>;

export interface CartLine {
  dishId: string;
  categoryId: string;
  quantity: number;
  modifications: string;
  // Optional so existing localStorage carts still parse.
  dietaryTags?: DietaryConcern[];
  // True when the user explicitly overrode a confirmed-allergen contradiction.
  overridden?: boolean;
}
export interface Cart {
  menuId: string;
  lines: CartLine[];
}

// SavedOrder is a SNAPSHOT of a finalized order. Names and prices are frozen
// at finalize time so the history view doesn't break if the underlying menu
// is re-parsed or deleted upstream.
export interface SavedOrderLine {
  dishId: string;
  nameOriginal: string;
  nameTranslated: string;
  quantity: number;
  modifications: string;
  dietaryTags: DietaryConcern[];
  priceText: string | null;
  price: number | null;
  overridden: boolean;
  // Snapshot of the dish's confirmed allergens at finalize time. Required
  // to render the override-kitchen-confirm warning on the hand-off screen
  // (and on the saved order view) even after the underlying menu has been
  // re-parsed or deleted upstream.
  allergens: Allergen[];
  // Snapshot of the dish's parser confidence so history view can still flag
  // uncertain translations.
  confidence: number;
}

export interface SavedOrder {
  id: string;
  menuId: string;
  restaurantName: string | null;
  sourceLanguage: string;
  targetLanguage: string;
  currency: string | null;
  total: number;
  lines: SavedOrderLine[];
  finalizedAt: string; // ISO 8601
  syncedAt: string | null; // ISO 8601 or null when never synced
}

export function dishMinConfidence(menu: Menu): number {
  let min = 1;
  for (const c of menu.categories) {
    for (const d of c.dishes) {
      if (d.confidence < min) min = d.confidence;
    }
  }
  return menu.categories.length === 0 ? 0 : min;
}

export function findDish(menu: Menu, dishId: string): { dish: Dish; category: Category } | null {
  for (const c of menu.categories) {
    const d = c.dishes.find((x) => x.id === dishId);
    if (d) return { dish: d, category: c };
  }
  return null;
}
